import { isDefined } from '~/utils/guards/isDefined';
import type { OptionalMoneyFragment } from '~/utils/shop/formatMoney';

export const APPLIED_TAX_BREAKDOWN_SHOP_STORE = 'amer_en';

export interface AppliedTaxRow {
  label: string;
  amount: OptionalMoneyFragment;
}

type AppliedTaxSource = {
  label?: string | null;
  title?: string | null;
  amount?: OptionalMoneyFragment | null;
};

function getSourceLabel(source: AppliedTaxSource): string {
  return source.label ?? source.title ?? '';
}

/** True when cart/checkout should list each applied tax on its own summary row. */
export function isAppliedTaxBreakdownStore(shopStore: string): boolean {
  return shopStore === APPLIED_TAX_BREAKDOWN_SHOP_STORE;
}

/** Returns cart/order tax rows with a positive amount. */
export function filterAppliedTaxRows(
  taxes: ReadonlyArray<AppliedTaxSource | null | undefined> | null | undefined
): AppliedTaxRow[] {
  return (taxes ?? [])
    .filter(isDefined)
    .filter(
      (entry: AppliedTaxSource) =>
        isDefined(entry.amount) &&
        isDefined(entry.amount.value) &&
        entry.amount.value > 0
    )
    .map((entry: AppliedTaxSource) => ({
      label: getSourceLabel(entry),
      amount: entry.amount!
    }));
}

/** Dictionary key for overriding a Magento tax label, e.g. `cart.label.tax.magento.sales_and_use`. */
export function buildAppliedTaxMagentoLabelDictionaryKey(
  magentoLabel: string
): string {
  const slug = magentoLabel
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return `cart.label.tax.magento.${slug}`;
}

/**
 * Summary label for one applied tax row (amer_en breakdown).
 * Format: `${dictionaryLabel} (${magentoLabel})` with optional dictionary override for the Magento label.
 */
export function getAppliedTaxDisplayLabel(
  dictionaryLabel: string,
  magentoLabel: string,
  magentoLabelDictionaryKey: string,
  textDictionary?: Record<string, string | undefined>
): string {
  const displayMagentoLabel =
    textDictionary?.[magentoLabelDictionaryKey] ?? magentoLabel;

  return `${dictionaryLabel} (${displayMagentoLabel})`;
}

const RATE_LABEL_PLACEHOLDER = '{rate}';

function pickOrderTaxRateLabelTemplate(
  textDictionary?: Record<string, string | undefined>
): string {
  const vatRateLabel = textDictionary?.['cart.vatRate.label'];
  if (isDefined(vatRateLabel) && vatRateLabel.length > 0) {
    return vatRateLabel;
  }

  const customerTaxRate = textDictionary?.['customerAccount.taxrate'];
  if (isDefined(customerTaxRate) && customerTaxRate.length > 0) {
    return customerTaxRate;
  }

  return 'zzgl. {rate}% MwSt.';
}

/** Rate-based tax label for order details, e.g. `zzgl. 10.5% MwSt.` */
export function formatOrderTaxRateLabel(
  rate: number | undefined,
  textDictionary?: Record<string, string | undefined>
): string {
  if (!isDefined(rate) || rate <= 0) {
    return textDictionary?.['cart.label.tax'] ?? 'Plus taxes';
  }

  const translation = pickOrderTaxRateLabelTemplate(textDictionary);

  if (!translation.includes(RATE_LABEL_PLACEHOLDER)) {
    return translation;
  }

  return translation.replace(RATE_LABEL_PLACEHOLDER, String(rate));
}

/**
 * Order-details tax label for amer_en: rate label plus Magento tax type in parentheses.
 * Example: `zzgl. 10.5% MwSt. (Sales and Use)` with optional `cart.label.tax.magento.*` override.
 */
export function getOrderTaxDisplayLabel(
  rate: number | undefined,
  magentoLabel: string,
  textDictionary?: Record<string, string | undefined>
): string {
  const rateLabel = formatOrderTaxRateLabel(rate, textDictionary);

  if (!magentoLabel) {
    return rateLabel;
  }

  const magentoLabelDictionaryKey =
    buildAppliedTaxMagentoLabelDictionaryKey(magentoLabel);
  const displayMagentoLabel =
    textDictionary?.[magentoLabelDictionaryKey] ?? magentoLabel;

  return `${rateLabel} (${displayMagentoLabel})`;
}

/** Sums all applied tax row amounts into one money object. */
export function sumAppliedTaxAmounts(
  rows: ReadonlyArray<AppliedTaxRow>
): OptionalMoneyFragment {
  if (!rows.length) {
    return { value: 0, currency: null };
  }

  const currency = rows[0].amount.currency;
  const value = rows.reduce(
    (sum: number, row: AppliedTaxRow) => sum + (row.amount.value ?? 0),
    0
  );

  return { currency, value };
}
